import { useState, useRef, useEffect } from "react"
import ScannerInput from "./ScannerInput/ScannerInput"
import { IconKeyBoard, IconSpinner } from "theme/common/Icon.styled"
import { ButtonStyled } from "theme/common/Button.styled"
import Skeleton from "react-loading-skeleton"
import { ProductCounStyled } from "components/OrderDetail/ProductOrderConfirm/ProductOrderDetail.styled"
import {
  MessageLoadSim,
  LoadingContainer,
  LoadingProgressBar,
  SpinnerContainer,
  TextContainer,
  LoadingMainText,
  LoadingSubText,
} from "./ScannerItems.styled"
import { ItemOrderType } from "types/OrderType"
import Swal from "sweetalert2"

interface ScannerItemsProps {
  onClicProcessProduct: (item: any) => void
  source?: any
  itemOrder?: ItemOrderType
  allOrderItems?: any
  remainingItems?: ItemOrderType[]
}

const ScannerItems = ({
  onClicProcessProduct,
  source,
  itemOrder,
  allOrderItems,
  remainingItems,
}: ScannerItemsProps) => {
  const [processedSeries, setProcessedSeries] = useState<string[]>([])
  const refInputSacnner = useRef<any>(null)
  const refInputScannerManual = useRef<any>(null)
  const [showManual, setShowManual] = useState(false)
  // const refModal = useRef<any>(null)
  const [loadingSerie, setLoadingSerie] = useState(false)

  // Validamos si ya hay series procesadas en el item (solo para un item especifico)
  useEffect(() => {
    const custom: any = itemOrder?.custom
    if (custom?.scannedSeries && Array.isArray(custom.scannedSeries)) {
      setProcessedSeries(custom.scannedSeries)
    }
  }, [itemOrder])

  const getValdateSerie = async (serieScanner: string): Promise<any> => {
    setLoadingSerie(true)

    // Simular tiempo de validación
    await new Promise((resolve) => setTimeout(resolve, 500))
    setLoadingSerie(false)

    // Si pasamos los restates, estamos en escaneo múltiple (global mode)
    if (remainingItems) {
      const skus = serieScanner
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      const validItems: any[] = []
      const invalidSkus: string[] = []

      const quantityMap: Record<string, number> = {}
      const itemMap: Record<string, any> = {}
      remainingItems.forEach(item => {
         if (!item) return
         const cleanSku = item.sku?.replace(/ /g, "").trim() || ""
         quantityMap[cleanSku] = (quantityMap[cleanSku] || 0) + (item.quantity || 1)
         itemMap[cleanSku] = item
      })

      skus.forEach((sku) => {
        const cleanSku = sku.replace(/ /g, "").trim()
        if (quantityMap[cleanSku] && quantityMap[cleanSku] > 0) {
          validItems.push(itemMap[cleanSku])
          quantityMap[cleanSku] -= 1
        } else {
          invalidSkus.push(sku)
        }
      })

      if (invalidSkus.length > 0) {
        return { error: true, message: `Códigos inválidos o ya procesados: ${invalidSkus.join(", ")}`, validItems }
      }
      return { error: false, message: "OK", validItems }
    }

    // Validación estricta original (single item mode)
    const cleanSerieScanner = serieScanner.replace(/ /g, "").trim()
    const cleanSku = itemOrder?.sku?.replace(/ /g, "").trim() || ""

    if (cleanSerieScanner === cleanSku) {
      return {
        error: false,
        message: itemOrder?.sku || "",
        validItems: [itemOrder],
      }
    }

    return {
      error: true,
      message: "Código no corresponde al producto",
      validItems: [],
    }
  }

  const handleValidateScanner = async (serieScanner: string) => {
    // Si no es multiple y ya se ingresó este código
    if (!remainingItems && processedSeries.includes(serieScanner)) {
      Swal.fire({
        icon: "warning",
        title: "⚠️ Código duplicado",
        html: `El código <strong>${serieScanner}</strong> ya fue ingresado anteriormente.`,
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#d33",
      })
      return
    }

    const result = await getValdateSerie(serieScanner)

    if (result?.validItems && result.validItems.length > 0) {
      result.validItems.forEach((item: any) => {
        if (!item) return

        let newSeries = [serieScanner]
        if (!remainingItems) {
          newSeries = [...processedSeries, serieScanner]
          setProcessedSeries(newSeries)
        }

        onClicProcessProduct({
          ...item,
          quantity: 1,
          scannedSeries: newSeries,
          currentScannedSeries: serieScanner,
          isConfirm: true,
        })
      })
    }

    if (result?.error) {
      Swal.fire({
        icon: "warning",
        title: "⚠️ Código no encontrado",
        html: `<p>${result.message}</p>`,
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#d33",
      })
    }
  }

  const add_manually = () => {
    if (!showManual) {
      setShowManual(true)
      setTimeout(() => {
        if (refInputScannerManual.current) {
          refInputScannerManual.current.expandInput()
          if (refInputScannerManual.current.setForceActive) {
            refInputScannerManual.current.setForceActive(true)
          }
          refInputScannerManual.current.focus()
        }
      }, 50)
    } else {
      setShowManual(false)
    }
  }

  const hasContext = itemOrder || remainingItems

  return (
    <>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {hasContext ? (
          <>
            {loadingSerie && (
              <MessageLoadSim>
                <LoadingContainer>
                  <LoadingProgressBar />
                  <SpinnerContainer>
                    <IconSpinner variant="big" style={{ color: "#2196f3" }} />
                  </SpinnerContainer>
                  <TextContainer>
                    <LoadingMainText>🔍 Validando SERIE...</LoadingMainText>
                    <LoadingSubText>Por favor espera mientras verificamos el producto</LoadingSubText>
                  </TextContainer>
                </LoadingContainer>
              </MessageLoadSim>
            )}
            <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "0.5rem" }}>
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <ScannerInput
                  ref={refInputSacnner}
                  onClickConfirmScanner={handleValidateScanner}
                  loadingValidateSerial={loadingSerie}
                  labelButton="Pistolear código"
                  placeholderInput="Esperando código(s)..."
                  variant="secondary"
                  isBarcodeMode={true}
                />
                {showManual && (
                  <ScannerInput
                    ref={refInputScannerManual}
                    onClickConfirmScanner={handleValidateScanner}
                    loadingValidateSerial={loadingSerie}
                    labelButton="Ingresar manualmente"
                    placeholderInput="Ingresar SKU(s)"
                    variant="secondary"
                    icon={<IconKeyBoard />}
                  />
                )}
              </div>
              <ButtonStyled
                variant="secondary"
                onClick={add_manually}
                style={{ padding: "0 0.75rem", backgroundColor: "#EAB30A", color: "#000", borderColor: "#EAB30A" }}
                title="Ingresar manualmente"
              >
                <IconKeyBoard />
              </ButtonStyled>
            </div>
            <span style={{ fontSize: "0.85rem", color: "#666", marginTop: "0.2rem", textAlign: "center" }}>
              Puede ingresar varios SKU separados por coma
            </span>
          </>
        ) : (
          <>
            <ProductCounStyled>
              <Skeleton width={150} height={20} />
            </ProductCounStyled>
            <Skeleton width={"100%"} count={1} height={44} />
          </>
        )}
      </div>
    </>
  )
}

export default ScannerItems
